import React, { useState } from "react";
import { Box, Typography, Paper, Stack, Button } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";

import CreateAutomationForm from "../forms/CreateAutomationForm";
import useAutomations from "../hooks/automationhooks/useAutomations";

/**
 * Stand‑alone page for creating a Journey Automation.
 * Re‑uses the existing <CreateAutomationForm/> so the validation & submission
 * logic are identical to the dialog version.
 */
export default function CreateAutomationPage() {
  const { brandId } = useParams();
  const navigate = useNavigate();

  const { handleCreateAutomationSubmit, handleGetUserData } = useAutomations();

  const [automationData, setAutomationData] = useState({
    brandId: brandId,
    status: "Pending",
    name: "New Journey",
    description: "",
    priority: "Low",
    schedule: {
      start: dayjs().add(10, "minute"),
      end: null,
    },
  });

  /* ────────────────────────────────────
   * Local validation (kept 1‑to‑1 with previous dialog):
   * ──────────────────────────────────── */
  const isFormValid = () => {
    const startDate = dayjs(automationData.schedule?.start);
    const endDate = dayjs(automationData.schedule?.end);

    if (!automationData.brandId) {
      return false;
    }

    if (automationData.schedule?.end) {
      return (
        automationData.name &&
        automationData.status &&
        automationData.priority &&
        automationData.schedule?.start &&
        startDate.isBefore(endDate) &&
        startDate.isAfter(dayjs())
      );
    }

    return (
      automationData.name &&
      automationData.status &&
      automationData.priority &&
      automationData.schedule?.start &&
      startDate.isAfter(dayjs())
    );
  };

  /* ────────────────────────────────────
   * Side effects
   * ──────────────────────────────────── */
  React.useEffect(() => {
    // Ensure we have user context loaded – required by handleCreateAutomationSubmit
    handleGetUserData?.();
  }, []);

  /* ────────────────────────────────────
   * Callbacks
   * ──────────────────────────────────── */
  const onCreateSuccess = (createdAutomation) => {
    const brandIdToUse = automationData.brandId || brandId;
    navigate(`/workflow/${brandIdToUse}/${createdAutomation.AutomationId}`);
  };

  const handleSubmit = () => {
    const brandIdToUse = automationData.brandId || brandId;
    handleCreateAutomationSubmit(automationData, brandIdToUse, onCreateSuccess);
  };

  return (
    <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
      <Paper elevation={3} sx={{ width: 640, p: 4 }}>
        <Typography variant="h4" fontWeight="bold" mb={2}>
          Create Journey
        </Typography>

        {/* Shared form component */}
        <CreateAutomationForm
          automationData={automationData}
          setAutomationData={setAutomationData}
        />

        {/* Actions */}
        <Stack direction="row" spacing={2} justifyContent="flex-end" mt={3}>
          <Button
            onClick={() =>
              (window.location.href = `https://icreate-campaign.com/admin/Campaigns/Index?app=1004&brand=${brandId}`)
            }
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!isFormValid()}
            onClick={handleSubmit}
          >
            Create
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}
